import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { Injectable } from '@angular/core';

import { AppConfigService } from '@core/appconfig.service';
import {
  StatefulService
} from '@core/services/stateful-service-management/stateful-service-interface/stateful-service-interface';
import { UserService } from '@core/user.service';
import { ApiResponse } from '@models/api-response';
import { ViewerAsset } from '@models/viewer-asset';
import { RoleGroup, RoleType } from '@shared/enums/role-type';
import { VariantType } from '@shared/enums/variant-type';
import { ViewerAssetType } from '@shared/enums/viewer-asset';
import { Helpers } from '@shared/helpers';
import {
  getEditionFromValue,
  IViewerMeta,
  ViewerEdition,
  ViewerLanguage,
  ViewerProduct
} from '@shared/viewer/enums/viewer-meta';
import { ViewerConfigurationModel } from '@shared/viewer/viewer-configuration-model';
import { AsyncSubject, BehaviorSubject, fromEvent, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay } from 'rxjs/operators';
import { GradeTypeDesignation } from './enums/grade-type-designation';
import { ViewerProductTypes, ViewerProductTypesMap } from './enums/viewer-product-types';
import { ViewerHelpers } from './viewer-helpers';

/** Contains State */
@Injectable({
  providedIn: 'root',
})
export class ViewerService implements StatefulService {

  toggleAssetsSubject$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  viewerAssetsSubject$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  viewerAssets$: Observable<ViewerAsset[]> = this.viewerAssetsSubject$.asObservable();

  /** Set by ViewerComponent through QueryParams */
  language: string;
  /** Set by ViewerComponent through QueryParams */
  topicKey: string;
  /** Set by ViewerComponent through QueryParams */
  componentId: string;
  /** Set by getViewerMetadata */
  grade: string;

  pdfConfig: ViewerConfigurationModel = {
    assetType: ViewerAssetType.Pdf,
    includeNavigation: false,
    includeZoom: false,
    includeDigitalInk: false,
    enableDigitalInk: false,
    includePrint: false,
    includeAdditionalInfo: false,
    includeAudio: false,
    zoomMin: 50,
    zoomMax: 200,
    zoomIncrement: 10,
  };

  imageConfig: ViewerConfigurationModel = {
    assetType: ViewerAssetType.Image,
    includeNavigation: false,
    includeZoom: true,
    includeDigitalInk: true,
    enableDigitalInk: true,
    includePrint: false,
    includeAdditionalInfo: false,
    includeAudio: false,
    zoomMin: 50,
    zoomMax: 200,
    zoomIncrement: 10,
  };

  imagePrintConfig: ViewerConfigurationModel = {
    assetType: ViewerAssetType.ImagePrint,
    includeNavigation: false,
    includeZoom: false,
    includeDigitalInk: true,
    enableDigitalInk: true,
    includePrint: true,
    includeAdditionalInfo: false,
    includeAudio: false,
    zoomMin: 50,
    zoomMax: 200,
    zoomIncrement: 10,
  };

  videoCanvasConfig: ViewerConfigurationModel = {
    assetType: ViewerAssetType.VideoCanvas,
    includeNavigation: false,
    includeZoom: false,
    includeDigitalInk: true,
    enableDigitalInk: true,
    includePrint: false,
    includeAdditionalInfo: false,
    includeAudio: false,
    zoomMin: 50,
    zoomMax: 200,
    zoomIncrement: 10,
  };

  defaultConfig: ViewerConfigurationModel = {
    assetType: null,
    includeNavigation: false,
    includeZoom: false,
    includeDigitalInk: false,
    enableDigitalInk: false,
    includePrint: false,
    includeAdditionalInfo: false,
    includeAudio: false,
    zoomMin: 50,
    zoomMax: 200,
    zoomIncrement: 10,
  };


  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
    private userService: UserService,
    private appConfigService: AppConfigService,
  ) {}

  private getAssetApiUrl(metadata: IViewerMeta, loadAllAssets: boolean = false): string {
    const { product, viewerVariant } = metadata;
    const assetType: string = this.userService.user.viewingAsRole === RoleType.Parent
      ? 'parent-viewer-asset'
      : 'viewer-asset';
    // check for toggles for assets links

    if (!loadAllAssets && this.topicKey) {
      return `${this.appConfig.apiUrl}/digital-resources/lesson-asset/product/${product}/grade/${viewerVariant}/topic/${this.topicKey}`;
    }

    return `${this.appConfig.apiUrl}/digital-resources/superkids/${assetType}/product/${product}/grade/${viewerVariant}`;
  }

  getAssetJsonUrl(metadata: IViewerMeta): string {
    const grade = GradeTypeDesignation[metadata.grade].toLocaleLowerCase();
    return `${this.appConfigService.assetUrl}content/grade${grade}/${metadata.series}/viewer/data.json`;
  }

  getViewerMetadata(componentId: string, language: ViewerLanguage = ViewerLanguage.en): IViewerMeta {
    const meta: IViewerMeta = {
      grade: ViewerHelpers.getGradeType(this.grade),
      viewerVariant: ViewerHelpers.mapComponentIdToViewerVariantType(componentId),
      language,
      product: this.getProductTypeFromComponentId(componentId)
    };
    const matches = componentId.match(ViewerHelpers.componentIdFullPattern);

    if (matches && matches.length > 4) {
      meta.edition = getEditionFromValue(matches[3]);
      meta.series = this.getSeriesFromProductStem(matches[1], matches[2], meta.edition);
      meta.variant = ViewerHelpers.mapComponentIdToVariantType(componentId);

      if (!this.grade) {
        // Special snowflake variant types do not get "grade" set so that we can use variant type later.
        if (matches[4]?.substring(0, 1) === 'g' && matches[4].includes('-')) {
          meta.grade = ViewerHelpers.mapGradeToGradeTypeForViewer(matches[4].substring(1, 2));
        } else if (matches[4]?.substring(0, 1) === 'g' && !matches[4].includes('-')) {
          meta.grade = ViewerHelpers.mapGradeToGradeTypeForViewer(matches[4].substring(1));
        } else {
          meta.grade = ViewerHelpers.mapGradeToGradeTypeForViewer(null);
        }
      }
    }

    this.grade = meta.grade;
    return meta;
  }

  // Load the assets for a series, grade, and language from bootstrap.
  getAssets(loadAllAssets: boolean = false): Observable<ApiResponse<ViewerAsset[]>> {
    const normalizedComponentId = this.componentId?.toLocaleLowerCase();
    const language = this.language ? ViewerLanguage[this.language] : ViewerLanguage.English;
    const metadata = this.getViewerMetadata(normalizedComponentId, language);
    const match = normalizedComponentId.slice(-3);
    const removeLanguageFilter = match === '_tl' || match === '_pp';

    // check for toggles for assets links
    if (!loadAllAssets && this.topicKey) {
      return this.getToggleAssetsFromApi(metadata);
    }
    return this.getViewerAssetsFromApi(metadata, removeLanguageFilter, loadAllAssets);
  }

  getMediaArrayBuffer(uri: string): Observable<ArrayBuffer> {
    return this.http.get(uri, { responseType: 'arraybuffer' });
  }

  getMediaBlobUri(uri: string): Observable<string> {
    return this.http
      .get(uri, { responseType: 'blob' })
      .pipe(
        map((blob: Blob) => URL.createObjectURL(blob))
      );
  }

  getAssetFilenameUrl(
    asset: ViewerAsset, subDirectory: string = 'pdf',
    language: ViewerLanguage = ViewerLanguage.en
  ): string {
    let { fileName } = asset;
    let langSuffix = '';
    const normalizedComponentId = asset.componentId.toLocaleLowerCase();
    const metadata = this.getViewerMetadata(normalizedComponentId, language);
    if ((metadata.series.substring(0, 2) === 'hw' || metadata.series.substring(0, 5) === 'laesc')
        && metadata.edition
        && metadata.edition !== ViewerEdition.TexasSampling
        && !metadata.series.includes('hw2025')) {
      langSuffix = asset.language === ViewerLanguage.en || asset.language === ViewerLanguage.English
        ? '/en'
        : '/es';
    }

    if (!metadata.series.includes('hw2025')
      && (metadata.variant === VariantType.Grade2C || metadata.variant === VariantType.Grade2M)) {
      metadata.variant = VariantType.Grade2;
    }

    // This should not be repeated outside of the Viewer.
    // This is a one-off check since the value of these VariantTypes differ between ZBPortal and Digital Resources
    // The Spanish version of these 2 Variants have the same value of their non-Spanish Variant in DR.
    if (metadata.variant === VariantType.GradeKto2Spanish) {
      metadata.variant = VariantType.GradeKto2;
    } else if (metadata.variant === VariantType.Grade3to5Spanish) {
      metadata.variant = VariantType.Grade3to5;
    }

    const variantOrLevelDir = metadata.variant.toLocaleLowerCase();

    if (asset.viewerAssetType === ViewerAssetType.Canvas) {
      fileName = `${asset.assetId}.png`;
    } else if (asset.viewerAssetType === ViewerAssetType.SuperSmart) {
      const isStudent = this.userService.user.isViewingAsRoleGroup(RoleGroup.students);
      fileName = `${asset.assetId}.html?enableQuestions=${!isStudent}`;
    }

    // Texas sampling product folders need 'txs' suffix
    const productLine = metadata.edition === ViewerEdition.TexasSampling || metadata.edition === ViewerEdition.Alabama
      ? `${metadata.series}${metadata.edition}`
      : metadata.series;
    const productLineFolder = Helpers.getProductLineFolder(productLine);

    return `${this.appConfigService.assetUrl}content/${variantOrLevelDir}/${productLineFolder}/viewer/${subDirectory}${langSuffix}/${fileName}`;
  }

  loadImage(src: string): Observable<HTMLImageElement> {
    const subject = new AsyncSubject<HTMLImageElement>();
    const image = new Image();

    fromEvent(image, 'load')
      .subscribe((e) => {
        subject.next(e.currentTarget as HTMLImageElement);
        subject.complete();
      });
    fromEvent(image, 'error')
      .subscribe((e) => {
        subject.error(e);
      });

    image.src = src;
    return subject.asObservable();
  }

  loadImageAsBlob(src: string): Observable<string> {
    const subject = new AsyncSubject<string>();
    return this.http.get(src, { responseType: 'blob' })
      .pipe(
        mergeMap((blob: Blob) => {
          const reader = new FileReader();

          fromEvent(reader, 'load').subscribe(() => {
            subject.next(reader.result.toString());
            subject.complete();
          });
          reader.readAsDataURL(blob);

          return subject.asObservable();
        }),
        catchError((err) => {
          console.error(err);
          subject.error('Error loading background image.');
          return subject.asObservable();
        })
      );
  }

  // Retrieves the asset manifest from the ZBPortal-Api.
  getToggleAssetsFromApi(metadata: IViewerMeta): Observable<ApiResponse<ViewerAsset[]>> {
    const arf = this.getAssetApiUrl(metadata);
    return this.http.get<ApiResponse<ViewerAsset[]>>(arf)
      .pipe(
        map((res) => {
          this.toggleAssetsSubject$.next(res.response);
          return new ApiResponse<ViewerAsset[]>(true, {
            response: res.response,
            messages: res.messages,
          });
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          return of(new ApiResponse<ViewerAsset[]>(false, err.error));
        }),
        shareReplay()
      );
  }

  // Retrieves the asset manifest from the ZBPortal-Api.
  getViewerAssetsFromApi(
    metadata: IViewerMeta, removeLanguageFilter: boolean,
    loadAllAssets: boolean = false
  ): Observable<ApiResponse<ViewerAsset[]>> {
    // check for toggles for assets links
    let languageFilter = removeLanguageFilter;
    if (!loadAllAssets && this.topicKey) {
      languageFilter = true;
    }
    const ur = this.getAssetApiUrl(metadata, loadAllAssets);
    return this.http.get<ApiResponse<ViewerAsset[]>>(ur)
      .pipe(
        map(res => ({
          response: res.response
            .filter(item => languageFilter
                || (item.language === ViewerHelpers.convertViewerLanguageForApiAssets(metadata.language))),
          messages: res.messages,
        })),
        map((filteredResponse) => {
          this.viewerAssetsSubject$.next(filteredResponse.response);
          return new ApiResponse<ViewerAsset[]>(true,
            { response: filteredResponse.response,
              messages: filteredResponse.messages });
        }),
        catchError((err: HttpErrorResponse) => {
          console.error(err);
          return of(new ApiResponse<ViewerAsset[]>(false, err.error));
        }),
        shareReplay()
      );
  }

  /**
   * Gets the series based on product line information.
   *
   * Because of changes / breaks in specification over the years, we need to support
   * legacy directory structure along with special directory structures.
   */
  private getSeriesFromProductStem(stem: string, year: string, edition: ViewerEdition): ViewerProduct | string {
    let series = ViewerProduct[stem] ? `${ViewerProduct[stem]}${year}` : `${stem}${year}`;
    if (edition === ViewerEdition.TexasSampling && stem === 'laesc') {
      series = `laesc${year}`;
    }

    if (stem === 'hw' && year === '2025') {
      series = `${series}n`;
    }
    // The laesc2026 series does not follow the other HW products and is just "laesc2026" instead of "hw2026"
    if (stem === 'laesc' && year === '2026') {
      series = `laesc${year}`;
    }

    return series;
  }

  reset() {
    this.toggleAssetsSubject$.next([]);
    this.viewerAssetsSubject$.next([]);
    this.language = '';
    this.topicKey = '';
    this.componentId = '';
    this.grade = '';
  }
  determineViewerConfiguration(asset: ViewerAsset): ViewerConfigurationModel {
    let configuration: ViewerConfigurationModel;

    switch (asset.viewerAssetType) {
    case ViewerAssetType.Pdf:
    case ViewerAssetType.PdfLocked: {
      configuration = this.pdfConfig;
      break;
    }
    case ViewerAssetType.Image: {
      configuration = this.imageConfig;
      break;
    }
    case ViewerAssetType.ImagePrint: {
      configuration = this.imagePrintConfig;
      break;
    }
    case ViewerAssetType.VideoCanvas: {
      configuration = this.videoCanvasConfig;
      break;
    }
    default: {
      configuration = this.defaultConfig;
      configuration.assetType = asset.viewerAssetType;
    }
    }

    // determine if navigation should be included based on the asset fields
    if (asset?.navigationToggles
      && (!!asset.navigationToggles.next || !!asset.navigationToggles.previous)) {
      configuration.includeNavigation = true;
    }

    // determine if audio should be included based on the asset fields
    if (asset?.relatedAudio?.length > 0) {
      configuration.includeAudio = true;
    } else {
      configuration.includeAudio = false;
    }

    // determine if additional info section should be included based on the asset having additionalInfo provided
    if (asset?.additionalInfo?.length) {
      configuration.includeAdditionalInfo = true;
    }

    // if no controls will show, we'll just use default configuration.
    if (!this.showViewerControls(configuration)) {
      configuration = this.defaultConfig;
    }

    return configuration;

  }

  private showViewerControls(configuration: ViewerConfigurationModel): boolean {
    return configuration.includeNavigation
      || configuration.includeZoom
      || configuration.includeDigitalInk
      || configuration.includePrint
      || configuration.includeAdditionalInfo
      || configuration.includeAudio;
  }

  getProductTypeFromComponentId(componentId: string): ViewerProductTypes {
    const componentIdLowerCase = componentId ? componentId.toLocaleLowerCase() : '';
    const componentSegments = componentIdLowerCase.split('_').map(s => s.toLocaleUpperCase());
    if (componentSegments && componentSegments.length > 0) {
      return ViewerProductTypesMap[componentSegments[0]]
        ? ViewerProductTypesMap[componentSegments[0]]
        : ViewerProductTypes.None;
    }
    return ViewerProductTypes.None;
  }
}
